import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Languages,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  RefreshCw,
  FileWarning,
  Shield,
  FileText,
  FolderClosed,
} from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

// ─── Per-doc-type widget personality ────────────────────────────
//
// One config map keeps the visual + copy + behaviour differences
// between contract / privacy / annex centralised. Adding a fourth
// doc type later means adding a row here.
const DOC_TYPE_META = {
  contract: {
    title: "Copertura linguistica CMS — Contratto",
    Icon: FileText,
    iconBg: "bg-blue-500/15",
    iconColor: "text-blue-400",
    aiCostPerLang: 0.1,
    aiSecPerLang: 30,
    seedDefaultsAvailable: true,  // ``/seed-defaults`` covers contracts
  },
  privacy: {
    title: "Copertura linguistica CMS — Informativa Privacy",
    Icon: Shield,
    iconBg: "bg-emerald-500/15",
    iconColor: "text-emerald-400",
    aiCostPerLang: 0.05,
    aiSecPerLang: 25,
    seedDefaultsAvailable: false, // Privacy PDFs render on-the-fly
  },
  annex: {
    title: "Copertura linguistica CMS — Allegati",
    Icon: FolderClosed,
    iconBg: "bg-purple-500/15",
    iconColor: "text-purple-400",
    aiCostPerLang: 0,
    aiSecPerLang: 0,
    seedDefaultsAvailable: false, // Manual upload only
  },
};

// ─── Status pill metadata ───────────────────────────────────────
//
// The backend returns 3 statuses per language:
// • covered                      — active CMS doc present (green)
// • uncovered_with_translation   — AI cache exists, just needs seed-defaults (amber)
// • missing_translation          — needs Claude generation (red)
const STATUS_META = {
  covered: {
    label: "Coperto",
    pillCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    Icon: CheckCircle2,
  },
  uncovered_with_translation: {
    label: "Cache pronta",
    pillCls: "bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40",
    Icon: FileWarning,
  },
  missing_translation: {
    label: "Mancante",
    pillCls: "bg-red-500/15 text-red-300 border-red-500/40",
    Icon: AlertTriangle,
  },
};

const POLL_INTERVAL_MS = 5000;

export const CmsCoverageWidget = ({ docType = "contract", onChanged }) => {
  const { getAuthHeader, user } = useAuth();
  const canWrite = user?.role === "super_admin" || user?.role === "admin";
  const meta = DOC_TYPE_META[docType] || DOC_TYPE_META.contract;
  const TypeIcon = meta.Icon;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [jobProgress, setJobProgress] = useState(null);
  const pollerRef = useRef(null);
  // Refs to break the fetchCoverage ↔ startPolling circular dep
  // without triggering React's "use before init" temporal dead zone.
  const fetchCoverageRef = useRef(null);
  const startPollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  const fetchCoverage = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/admin/legal-documents/cms-coverage?doc_type=${docType}`,
        getAuthHeader()
      );
      setData(r.data);
      if (r.data?.running_job && !pollerRef.current) {
        setGenerating(true);
        setJobProgress(r.data.running_job);
        startPollingRef.current?.(r.data.running_job.job_id);
      }
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, docType]);

  // Keep ref in sync so startPolling can call the latest fetchCoverage
  // without listing it as a dep (which would re-create the function on
  // every render).
  useEffect(() => {
    fetchCoverageRef.current = fetchCoverage;
  }, [fetchCoverage]);

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollerRef.current = setInterval(async () => {
        try {
          const r = await axios.get(
            `${API}/admin/legal-documents/translation-jobs/${jobId}`,
            getAuthHeader()
          );
          setJobProgress(r.data);
          if (r.data.status !== "running") {
            stopPolling();
            const tr = r.data.translated || 0;
            const fl = r.data.failed || 0;
            if (fl > 0) {
              toast.warning(`Generazione completata: ${tr} tradotte, ${fl} fallite`);
            } else if (tr > 0) {
              const next = meta.seedDefaultsAvailable
                ? " — pubblicazione CMS in corso…"
                : "";
              toast.success(`${tr} traduzioni AI generate${next}`);
            }
            if (tr > 0 && meta.seedDefaultsAvailable) {
              try {
                const s = await axios.post(
                  `${API}/admin/legal-documents/seed-defaults`,
                  {},
                  getAuthHeader()
                );
                const seeded = s.data?.seeded ?? 0;
                if (seeded > 0) {
                  toast.success(`${seeded} documenti pubblicati nel CMS`);
                }
              } catch (e) {
                toast.error(
                  "Traduzioni generate ma pubblicazione CMS fallita: " +
                    (e.response?.data?.detail || "errore sconosciuto")
                );
              }
            }
            setGenerating(false);
            setJobProgress(null);
            await fetchCoverageRef.current?.();
            if (onChanged) onChanged();
          }
        } catch (e) {
          if (e.response?.status === 404) {
            stopPolling();
            setGenerating(false);
            setJobProgress(null);
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [getAuthHeader, stopPolling, onChanged, meta.seedDefaultsAvailable]
  );

  // Mirror startPolling into a ref so fetchCoverage (declared earlier)
  // can invoke it without listing it as a dep.
  useEffect(() => {
    startPollingRef.current = startPolling;
  }, [startPolling]);

  useEffect(() => {
    fetchCoverage();
    return () => stopPolling();
  }, [fetchCoverage, stopPolling]);

  const handleGenerateMissing = async () => {
    if (!data || !canWrite) return;
    const missing = data.by_language
      .filter((r) => r.status === "missing_translation" && r.in_ai_service_supported)
      .map((r) => r.lang);
    if (missing.length === 0) {
      toast.info("Nessuna lingua AI-traducibile da generare");
      return;
    }
    const ok = window.confirm(
      `Generare le traduzioni AI mancanti per ${meta.title.split('—').pop().trim()}\n\n` +
        `Lingue (${missing.length}): ${missing.join(", ")}\n` +
        `Operazione in background: ~${meta.aiSecPerLang}s/lingua × ${missing.length} ≈ ${missing.length * meta.aiSecPerLang}s\n` +
        `Costo stimato: ~$${(missing.length * meta.aiCostPerLang).toFixed(2)} (Claude Sonnet 4.5)\n\n` +
        `Puoi continuare a usare l'app: il job gira in background.`
    );
    if (!ok) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/legal-documents/generate-missing-translations?doc_type=${docType}`,
        null,
        getAuthHeader()
      );
      const jobId = r.data?.job_id;
      if (jobId) {
        toast.success(`Job avviato in background (${missing.length} lingue)`);
        setJobProgress({
          job_id: jobId,
          status: "running",
          translated: 0,
          skipped: 0,
          failed: 0,
          languages_requested: missing,
        });
        startPolling(jobId);
      } else {
        toast.info(r.data?.message || "Nessuna lingua da tradurre");
        setGenerating(false);
        await fetchCoverage();
      }
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(`Un job di traduzione ${docType} è già in corso`);
        const existingJobId = e.response?.data?.detail?.job_id;
        if (existingJobId) {
          startPolling(existingJobId);
          return;
        }
      } else {
        toast.error(e.response?.data?.detail || "Errore avvio job traduzione");
      }
      setGenerating(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!data || !canWrite) return;
    setSeeding(true);
    const t = toast.loading(`Pubblicazione CMS in corso…`);
    try {
      const r = await axios.post(
        `${API}/admin/legal-documents/seed-defaults`,
        {},
        getAuthHeader()
      );
      toast.dismiss(t);
      const seeded = r.data?.seeded ?? 0;
      const activated = r.data?.activated ?? 0;
      toast.success(`${seeded} documenti pubblicati (${activated} attivati)`);
      await fetchCoverage();
      if (onChanged) onChanged();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || "Errore pubblicazione CMS");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div
        className="bg-[#111] border border-gray-800 rounded-2xl p-5 flex items-center justify-center"
        data-testid={`cms-coverage-loading-${docType}`}
      >
        <Loader2 size={20} className="text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const {
    covered,
    total_languages,
    missing_translation,
    uncovered_with_translation,
    coverage_pct,
    by_language,
    ai_service_available,
  } = data;
  const allCovered = missing_translation === 0 && uncovered_with_translation === 0;

  // Count of missing langs that are actually AI-translatable (excludes
  // niche locales without AI support — relevant only for ``privacy``)
  const missingAITranslatable = by_language.filter(
    (r) => r.status === "missing_translation" && r.in_ai_service_supported
  ).length;

  const accent = allCovered
    ? "border-emerald-500/30"
    : missing_translation > 0
    ? "border-red-500/30"
    : "border-lojack-clause-500/30";

  const jobTotal =
    (jobProgress?.languages_requested?.length) || 0;
  const jobDone =
    (jobProgress?.translated || 0) + (jobProgress?.skipped || 0) + (jobProgress?.failed || 0);
  const jobPct = jobTotal > 0 ? Math.round((jobDone / jobTotal) * 100) : 0;

  return (
    <div
      className={`bg-[#111] border ${accent} rounded-2xl p-5`}
      data-testid={`cms-coverage-widget-${docType}`}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.iconBg} ${meta.iconColor}`}>
            <TypeIcon size={20} />
          </div>
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">
              {meta.title}
              {ai_service_available ? (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">
                  AI assist
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40">
                  upload only
                </span>
              )}
            </h3>
            <p className="text-gray-500 text-xs flex items-center gap-1.5">
              <Languages size={11} />
              <span data-testid={`cms-coverage-count-${docType}`} className="text-white font-semibold">
                {covered} / {total_languages}
              </span>
              lingue con documento attivo nel CMS ({coverage_pct}%)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchCoverage}
            disabled={generating || seeding}
            className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-50"
            title="Aggiorna stato"
            data-testid={`cms-coverage-refresh-${docType}`}
          >
            <RefreshCw size={12} />
          </button>
          {canWrite && uncovered_with_translation > 0 && !generating && meta.seedDefaultsAvailable && (
            <button
              onClick={handleSeedDefaults}
              disabled={seeding || generating}
              className="px-3 py-1.5 text-xs rounded-lg border border-lojack-clause-500/40 text-lojack-clause-300 hover:bg-lojack-clause-500/10 flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`cms-coverage-seed-btn-${docType}`}
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <FileWarning size={12} />}
              Pubblica cache ({uncovered_with_translation})
            </button>
          )}
          {canWrite && missingAITranslatable > 0 && ai_service_available && (
            <button
              onClick={handleGenerateMissing}
              disabled={generating || seeding}
              className="px-3 py-1.5 text-xs rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-200 hover:bg-blue-500/25 flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`cms-coverage-generate-btn-${docType}`}
            >
              {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {generating
                ? `In corso: ${jobDone}/${jobTotal}`
                : `Genera traduzioni AI mancanti (${missingAITranslatable})`}
            </button>
          )}
        </div>
      </div>

      {/* Job progress bar (only while a job is in flight) */}
      {generating && jobProgress && (
        <div
          className="mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30"
          data-testid={`cms-coverage-job-progress-${docType}`}
        >
          <div className="flex items-center justify-between text-xs text-blue-200 mb-2">
            <span className="flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" />
              Generazione AI in corso · job{" "}
              <code className="bg-black/30 px-1.5 py-0.5 rounded">{jobProgress.job_id}</code>
            </span>
            <span>
              {jobDone}/{jobTotal} ({jobPct}%)
            </span>
          </div>
          <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-400 transition-all"
              style={{ width: `${jobPct}%` }}
            />
          </div>
          <div className="text-[10px] text-blue-300/70 mt-1.5">
            ✓ {jobProgress.translated || 0} tradotte · ↪ {jobProgress.skipped || 0} saltate · ✗ {jobProgress.failed || 0} fallite
          </div>
        </div>
      )}

      {/* Coverage progress bar */}
      <div className="mb-4">
        <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              allCovered ? "bg-emerald-500" : missing_translation > 0 ? "bg-red-500" : "bg-lojack-clause-500"
            }`}
            style={{ width: `${coverage_pct}%` }}
            data-testid={`cms-coverage-progress-${docType}`}
          />
        </div>
      </div>

      {/* Per-language grid */}
      <div
        className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-2"
        data-testid={`cms-coverage-grid-${docType}`}
      >
        {by_language.map((row) => {
          const sMeta = STATUS_META[row.status] || STATUS_META.missing_translation;
          const Icon = sMeta.Icon;
          return (
            <div
              key={row.lang}
              className={`p-2.5 rounded-xl border text-xs ${sMeta.pillCls}`}
              title={`${row.name} — ${sMeta.label}`}
              data-testid={`cms-coverage-cell-${docType}-${row.lang}`}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="font-semibold text-white">{row.lang.toUpperCase()}</span>
                <Icon size={12} />
              </div>
              <p className="text-[10px] opacity-80 mt-0.5 truncate">{sMeta.label}</p>
            </div>
          );
        })}
      </div>

      {!canWrite && (
        <p
          className="text-gray-500 text-[11px] mt-4 italic"
          data-testid={`cms-coverage-readonly-note-${docType}`}
        >
          Sola lettura — serve ruolo Admin per generare traduzioni o pubblicare la cache.
        </p>
      )}

      {!ai_service_available && missing_translation > 0 && canWrite && (
        <p
          className="text-gray-400 text-[11px] mt-4 italic"
          data-testid={`cms-coverage-upload-hint-${docType}`}
        >
          Gli allegati sono caricati manualmente. Usa il pulsante &quot;Carica nuovo
          documento&quot; qui sotto per coprire le lingue mancanti.
        </p>
      )}
    </div>
  );
};

export default CmsCoverageWidget;
